/**
 * Phase 127 — Player's slot booking ledger at /arenax/me/bookings.
 *
 * Shows the entire booking lifecycle from the player's perspective.
 * Mocked payments auto-confirm via a single "Lock the slot" button.
 * Real Razorpay flows would open the Razorpay checkout (deferred to
 * next phase — current confirm-payment endpoint accepts empty body
 * for mock_payment=true bookings).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxPaymentModeBanner from './ArenaxPaymentModeBanner';
import {
  Calendar, Clock, IndianRupee, Loader2, ArrowRight, MapPin,
  CheckCircle2, X, RefreshCcw, AlertCircle,
} from 'lucide-react';

const STATE_LABELS = {
  requested: { label: 'Awaiting acceptance', color: '#92400e', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)' },
  accepted:  { label: 'Lock the slot · pay to confirm', color: '#0e7490', bg: 'rgba(6,182,212,0.10)', border: 'rgba(6,182,212,0.30)' },
  paid:      { label: 'Paid', color: '#065f46', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)' },
  confirmed: { label: 'Your ground is set ⚽', color: '#047857', bg: 'rgba(16,185,129,0.14)', border: 'rgba(16,185,129,0.40)' },
  live:      { label: 'Live now', color: '#0e7490', bg: 'rgba(6,182,212,0.18)', border: 'rgba(6,182,212,0.45)' },
  completed: { label: 'Played', color: '#065f46', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.18)' },
  declined:  { label: 'Declined by operator', color: '#7f1d1d', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.30)' },
  cancelled: { label: 'Cancelled', color: 'hsl(var(--ax-text-muted))', bg: 'var(--ax-card-bg-soft)', border: 'var(--ax-border-quiet)' },
  expired:   { label: 'Lock expired', color: 'hsl(var(--ax-text-faint))', bg: 'var(--ax-card-bg-soft)', border: 'var(--ax-border-quiet)' },
  refunded:  { label: 'Refunded', color: 'hsl(var(--ax-text-muted))', bg: 'var(--ax-card-bg-soft)', border: 'var(--ax-border-quiet)' },
};

function fmtDateTime(s) {
  const d = new Date(s);
  return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function ArenaxMyBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/arenax/me/slot-bookings');
      setBookings(r.data?.bookings || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load bookings');
    } finally { setLoading(false); }
  };

  const cancel = async (b) => {
    setActing(b.id);
    try {
      await api.post(`/arenax/me/slot-bookings/${b.id}/cancel`);
      toast.success('Booking cancelled');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    } finally { setActing(null); }
  };

  const payAndConfirm = async (b) => {
    setActing(b.id);
    try {
      // Mock-mode bookings (or zero-priced) confirm without Razorpay handshake.
      // Real-money bookings would open the Razorpay checkout SDK here and pass
      // (payment_id, order_id, signature) back to confirm-payment.
      await api.post(`/arenax/me/slot-bookings/${b.id}/confirm-payment`, {});
      toast.success(b.mock_payment
        ? 'Your ground is set ⚽ (mock payment)'
        : 'Your ground is set ⚽');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Payment failed');
    } finally { setActing(null); }
  };

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Calendar className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to see your bookings
          </h1>
          <button onClick={() => navigate('/arenax/auth/login')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}>
            Sign in
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      <section className="max-w-3xl mx-auto px-5 py-8 arenax-themed space-y-5" data-testid="arenax-my-bookings-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
                style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}>
              Your bookings
            </h1>
            <p className="text-sm mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              Slots you've requested, accepted, paid, or played.
            </p>
          </div>
          <button onClick={load}
                  className="text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"
                  style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                  data-testid="my-bookings-refresh">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {/* Phase 128 P1 — Payment mode banner (TEST / LIVE / MOCK) */}
        <ArenaxPaymentModeBanner variant="strip" hideWhenLive testId="my-bookings-payment-mode" />

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          </div>
        ) : bookings.length === 0 ? (
          <div className="arenax-card p-10 text-center rounded-2xl" data-testid="my-bookings-empty">
            <Calendar className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-text-faint))' }} />
            <h2 className="text-lg font-bold mb-1" style={{ color: 'hsl(var(--ax-text))' }}>
              No bookings yet
            </h2>
            <p className="text-sm mb-5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              Browse venues and request your first slot.
            </p>
            <button onClick={() => navigate('/arenax/venues')}
                    className="text-sm font-bold px-4 py-2.5 rounded-xl"
                    style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                    data-testid="my-bookings-browse-cta">
              Browse venues
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map(b => {
              const styleConfig = STATE_LABELS[b.state] || STATE_LABELS.requested;
              const canCancel = !['confirmed', 'live', 'completed', 'cancelled', 'declined', 'expired'].includes(b.state);
              const canPay = b.state === 'accepted';
              const lockMins = b.lock_expires_at
                ? Math.max(0, Math.round((new Date(b.lock_expires_at) - new Date()) / 60000))
                : 0;
              return (
                <div key={b.id} className="arenax-card p-5 rounded-xl flex items-start justify-between gap-4 flex-wrap"
                     data-testid={`my-bookings-row-${b.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 rounded-full"
                           style={{ color: styleConfig.color, background: styleConfig.bg, border: `1px solid ${styleConfig.border}` }}>
                        {styleConfig.label}
                      </div>
                      {b.state === 'requested' && lockMins > 0 && (
                        <div className="text-[10px]" style={{ color: 'hsl(var(--ax-text-faint))' }}>
                          · {lockMins}m left
                        </div>
                      )}
                    </div>
                    <div className="text-base font-bold mb-1" style={{ color: 'hsl(var(--ax-text))' }}>
                      {fmtDateTime(b.start_at)}
                    </div>
                    <div className="text-xs flex flex-wrap items-center gap-3"
                         style={{ color: 'hsl(var(--ax-text-muted))' }}>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(b.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {' – '}
                        {new Date(b.end_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {b.price ? (
                        <span className="inline-flex items-center gap-0.5 font-bold"
                              style={{ color: 'hsl(var(--ax-text))' }}>
                          <IndianRupee className="h-3 w-3" />{b.price}
                        </span>
                      ) : null}
                      <button onClick={() => navigate(`/arenax/venues/${b.venue_id}`)}
                              className="inline-flex items-center gap-1 underline-offset-2 hover:underline">
                        <MapPin className="h-3 w-3" /> View venue
                      </button>
                    </div>
                    {b.notes && (
                      <div className="text-[11px] mt-2 italic" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                        "{b.notes}"
                      </div>
                    )}
                    {b.state === 'declined' && b.decline_reason && (
                      <div className="text-[11px] mt-2 flex items-start gap-1"
                           style={{ color: '#7f1d1d' }}>
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {b.decline_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {canPay && (
                      <button onClick={() => payAndConfirm(b)}
                              disabled={acting === b.id}
                              className="text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                              style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                              data-testid={`my-bookings-pay-${b.id}`}>
                        {acting === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        {b.mock_payment ? 'Lock the slot (mock)' : 'Lock the slot'}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancel(b)}
                              disabled={acting === b.id}
                              className="text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-60"
                              style={{ color: 'hsl(var(--ax-text-muted))' }}
                              data-testid={`my-bookings-cancel-${b.id}`}>
                        {acting === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}
